import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import genericQuestions, { GenericTerminalQuestionFieldAnswers } from '../Generic/questions';

const migraineSpecificQuestions = [
  {
    field: 'migraine_safetyAcknowledgment',
    label:
      'Persistent or worsening headaches may signal a serious condition. Headaches that are new, progressively worsening, daily, worse in the morning, wake you from sleep, or are associated with vision changes, weakness, numbness, speech difficulty, seizures, persistent vomiting, or cognitive or personality changes require urgent in-person medical evaluation.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'do_not_continue'
        },
        {
          value: 'I have read the above information and wish to move forward',
          class: 'option-consent',
          key: 'proceed'
        }
      ]
    }
  },
  {
    field: 'migraine_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'migraine_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_consentPregnancy',
    label: 'Consent (pregnancy):',
    type: 'select',
    educationalSlide: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'do_not_proceed'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'proceed'
        }
      ]
    }
  },
  {
    field: 'migraine_diagnosedMigraine',
    label:
      'Do you have a known diagnosis of migraines or have headaches that have been unchanged for over 2 years?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_usualMigrainePattern',
    label: 'Are your headaches consistent with your usual migraine pattern?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes my symptoms are typical for my known migraines',
          key: 'typical'
        },
        {
          value: 'No, this headache feels different',
          key: 'different'
        }
      ]
    }
  },
  {
    field: 'migraine_hemiplegicOrBrainstemAura',
    label:
      'Have you ever been told you have hemiplegic migraine or migraine with brainstem aura?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_redFlagSymptoms',
    label:
      'Have you experienced any of the following symptoms with this headache or in the past 3 months? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'New weakness on one side of the body (face, arm, or leg)',
          key: 'new_weakness'
        },
        {
          value: 'Trouble speaking, finding words, or understanding speech',
          key: 'trouble_speaking'
        },
        {
          value: 'New or persistent numbness or tingling',
          key: 'numbness_tingling'
        },
        {
          value: 'Confusion, disorientation, or changes in mental status',
          key: 'confusion'
        },
        { value: 'Seizure activity', key: 'seizure' },
        {
          value:
            'Double vision or vision loss that is different from your usual migraine aura',
          key: 'vision_changes'
        },
        {
          value:
            'A sudden, severe headache described as the “worst headache of your life”',
          key: 'worst_headache'
        },
        {
          value:
            'A sudden, severe headache that became intense right away (“thunderclap headache”)',
          key: 'thunderclap'
        },
        {
          value:
            'A headache that has been progressively worsening or lasting longer than 72 hours',
          key: 'worsening_or_prolonged'
        },
        {
          value:
            'A headache triggered by exertion, coughing, or sexual activity',
          key: 'exertion_triggered'
        },
        {
          value:
            'Are you immunocompromised (e.g., on chemotherapy, transplant medications, chronic steroids, or immunosuppressive therapy)',
          key: 'immunocompromised'
        },
        {
          value: 'A recent head or neck injury or trauma',
          key: 'recent_trauma'
        },
        {
          value: 'Fever or neck stiffness associated with headache',
          key: 'fever_neck_stiffness'
        },
        {
          value: 'A new rash along with your headache',
          key: 'new_rash'
        },
        {
          value: 'New scalp tenderness or jaw pain while chewing',
          key: 'scalp_jaw_pain'
        },
        {
          value: 'I’m currently on blood thinners',
          key: 'blood_thinners'
        },
        {
          value: 'Light sensitivity',
          key: 'light_sensitivity'
        },
        {
          value: 'Sound sensitivity',
          key: 'sound_sensitivity'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'migraine_associatedSymptoms',
    label:
      'Do you commonly experience any of the following with your migraines? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Light sensitivity', key: 'light_sensitivity' },
        { value: 'Sound sensitivity', key: 'sound_sensitivity' },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'migraine_headacheOnset',
    label: 'When did this current headache begin?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Less than 24 hours ago', key: 'less_than_24_hours' },
        { value: '1–3 days ago', key: 'one_to_three_days' },
        { value: 'More than 72 hours ago', key: 'more_than_72_hours' },
        {
          value: 'No current symptoms, I’m preparing for my next episode',
          key: 'no_current_symptoms'
        }
      ]
    }
  },
  {
    field: 'migraine_painLocation',
    label: 'Where is the pain located?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Unilateral (one side)', key: 'unilateral' },
        { value: 'Bilateral (both sides)', key: 'bilateral' },
        { value: 'Frontal (forehead)', key: 'frontal' },
        { value: 'Occipital (back of head)', key: 'occipital' },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'migraine_painLocationOther',
    label: 'Tell us about the other location of your headaches',
    type: 'info',
    answer: {
      type: 'text',
      name: 'migraine_painLocationOther',
      placeholder: 'Describe the location'
    }
  },
  {
    field: 'migraine_aura',
    label:
      'Do your migraines start with an aura (visual, sensory, or speech symptoms before the headache)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Aura only', key: 'aura_only' },
        {
          value: 'Aura followed by headache',
          key: 'aura_followed_by_headache'
        },
        { value: 'No aura', key: 'no_aura' },
        { value: 'I am unsure', key: 'unsure' }
      ]
    }
  },
  {
    field: 'migraine_triggers',
    label:
      'Which of these commonly trigger your migraines? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Stress or anxiety', key: 'stress_anxiety' },
        { value: 'Sleep disturbances', key: 'sleep_disturbances' },
        {
          value: 'Hormonal changes (e.g., menstrual cycle)',
          key: 'hormonal_changes'
        },
        {
          value: 'Dietary factors (e.g., caffeine, alcohol, certain foods)',
          key: 'dietary_factors'
        },
        { value: 'Dehydration', key: 'dehydration' },
        {
          value: 'Bright lights or strong odors',
          key: 'lights_or_odors'
        },
        { value: 'Weather changes', key: 'weather_changes' },
        { value: 'Other', key: 'other' }
      ]
    }
  },
  {
    field: 'migraine_triggersOther',
    label: 'Tell us about your other triggers',
    type: 'info',
    answer: {
      type: 'text',
      name: 'migraine_triggersOther',
      placeholder: 'Describe your other triggers'
    }
  },
  {
    field: 'migraine_acuteMedFrequency',
    label: 'How often do you use acute migraine medications per month?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: '0–4 days/month', key: 'zero_to_four' },
        { value: '5–9 days/month', key: 'five_to_nine' },
        { value: '10–14 days/month', key: 'ten_to_fourteen' },
        { value: '15+ days/month', key: 'fifteen_plus' }
      ]
    }
  },
  {
    field: 'migraine_nsaidsTried',
    label:
      "Let's determine the best treatment option for you. Which acute (abortive) medications have you used for your migraines? Have you tried first line treatment for migraines such as NSAIDs such as ibuprofen, naproxen, or diclofenac?",
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have not tried these OR I have tried these and they work well',
          key: 'not_tried_or_effective'
        },
        {
          value:
            'I have tried these and they’re not effective or they cause side effects',
          key: 'not_effective'
        }
      ]
    }
  },
  {
    field: 'migraine_nsaidContraindications',
    label:
      'Do you have a history of kidney disease, stroke, TIA, heart disease, prior heart attack or uncontrolled hypertension?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_triptansTried',
    label:
      'Have you tried second line treatment for migraines called Triptans such as sumatriptan, rizatriptan, eletriptan, or zolmitriptan?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have not tried these OR I have tried these and they work well',
          key: 'not_tried_or_effective'
        },
        {
          value:
            'I have tried these and they’re not effective or they cause side effects',
          key: 'not_effective'
        }
      ]
    }
  },
  {
    field: 'migraine_triptanContraindications',
    label:
      'Do you have a history of Raynaud’s phenomenon, stroke, TIA, heart disease, prior heart attack or uncontrolled hypertension?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_gepantsTried',
    label:
      'Have you tried third line treatment for migraines called gepants such as ubrogepant, rimegepant, zavegepant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have not tried these OR I have tried these and they work well',
          key: 'not_tried_or_effective'
        },
        {
          value:
            'I have tried these and they’re not effective or they cause side effects',
          key: 'not_effective'
        }
      ]
    }
  },
  {
    field: 'migraine_gepantContraindications',
    label:
      'Do you have liver disease, or are you taking medications that strongly affect how your liver processes drugs (such as clarithromycin, itraconazole, or ritonavir)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_nauseaVomiting',
    label: 'Do you vomit or have severe nausea early in your migraine attacks?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes, frequently', key: 'yes_frequently' },
        { value: 'Sometimes', key: 'sometimes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_antidepressants',
    label:
      'Do you currently take any antidepressant medications, including SSRIs or SNRIs?\nExamples include sertraline (Zoloft), fluoxetine (Prozac), escitalopram (Lexapro), paroxetine (Paxil), citalopram (Celexa), venlafaxine (Effexor), duloxetine (Cymbalta), or desvenlafaxine (Pristiq)',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'migraine_serotoninSyndromeAck',
    label: `Common migraine medications can increase serotonin levels in the body. When these medications are used together with antidepressants such as SSRIs or SNRIs, there is a very low but possible risk of a rare condition called serotonin syndrome. Symptoms may include agitation, restlessness, confusion, tremor, muscle stiffness or twitching, rapid heart rate, changes in blood pressure, increased sweating, flushing, fever, nausea, or diarrhea. Most cases are mild, and serotonin syndrome is uncommon when migraine medications are taken as prescribed. If concerning symptoms occur, prompt medical evaluation is important.`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I understand and wish to avoid this class of medication',
          class: 'option-decline',
          key: 'avoid_class'
        },
        {
          value: 'I understand and wish to continue',
          class: 'option-consent',
          key: 'continue'
        }
      ]
    }
  },
  {
    field: 'migraine_otherInformation',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'migraine_otherInformation',
      placeholder: 'Optional'
    }
  },
  {
    field: 'migraine_treatmentConsent',
    label: 'Consent (Acute Migraine Treatment):',
    type: 'select',
    educationalSlide: `You are requesting treatment for acute migraine attacks. Migraine is a neurologic condition that can cause recurrent headaches and may be associated with symptoms such as nausea, vomiting, sensitivity to light or sound, and visual disturbances. Acute migraine treatment is intended to relieve symptoms during an active migraine attack and may include non-opioid pain relievers, triptans, CGRP-targeted therapies (including gepants or intranasal medications), and anti-nausea medications. These treatments are not intended to prevent future migraines.

Potential Benefits
➢ Relief of migraine pain during an active attack
➢ Reduced severity and duration of migraine symptoms
➢ Improved ability to function during daily activities
➢ Reduced need for emergency or urgent care visits

Potential Side Effects
While acute migraine medications are commonly used and often effective, they can cause side effects. In rare cases, medications may result in emergency room visits, hospitalization, or serious medical complications.

Common Side Effects (may vary by medication class):
➢ Nausea or upset stomach
➢ Drowsiness or fatigue
➢ Dizziness or lightheadedness
➢ Dry mouth
➢ Constipation or diarrhea
➢ Nasal irritation or injection-site reactions (with certain formulations)

Serious or Less Common Side Effects:
➢ Chest pain, shortness of breath, or changes in blood pressure
➢ Allergic reactions (rash, itching, swelling, difficulty breathing)
➢ Worsening headache or medication-overuse headache
➢ Liver function abnormalities

Risks and Considerations
➢ Cardiovascular Risk: Certain acute migraine medications (such as triptans) are not appropriate for individuals with a history of stroke, heart disease, uncontrolled hypertension, or vascular conditions. Your medical history and questionnaire responses are reviewed to reduce this risk.
➢ Medication Interactions: Some acute migraine treatments interact with antidepressants, antifungals, antibiotics, antivirals, or seizure medications. You must disclose all medications and supplements you are currently taking.
➢ Liver or Kidney Disease: Certain acute treatments may not be appropriate if you have liver or kidney disease, and alternative medications may be recommended.
➢ Pregnancy Considerations: Some acute migraine medications are not safe during pregnancy or while trying to conceive.
➢ Medication-Overuse Headache: Frequent use of acute migraine medications can worsen headaches over time. If this risk is identified, treatment adjustments or in-person evaluation may be recommended.

Your response to treatment and any side effects will be monitored through follow-up visits and secure messaging. You may be asked to report migraine severity, symptom relief, and medication effectiveness. Treatment may be adjusted or discontinued for safety reasons. You should seek urgent in-person or emergency medical care if you experience new neurologic symptoms, severe chest pain, fainting, or signs of an allergic reaction.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor.

I acknowledge that I have reviewed the potential benefits, risks, and side effects of acute migraine treatment medications. I understand that these treatments are intended for symptom relief during migraine attacks and do not prevent future migraines. I understand that no treatment guarantees complete relief and that medication selection is based on my medical history and responses. I agree to provide accurate health information and to participate in appropriate monitoring and follow-up.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'do_not_continue'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  }
] as const;

const migraineQuestions = [
  ...migraineSpecificQuestions,
  ...genericQuestions
] as const;

export type MigraineQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof migraineSpecificQuestions>
> &
  GenericTerminalQuestionFieldAnswers;

export default migraineQuestions;
